<!--
 * @Author: chengleilei
 * @Date: 2025-09-18 09:58:36
 * @Description: 客户管理列表
-->
<template>
    <div>
        <PageHeader title="客户管理" hidden-breadcrumb>
            <Button slot="action" type="warning" class="ivu-ml" icon="md-cloud-download" @click="onImport">
                批量导入维护人
            </Button>
        </PageHeader>
        <Card :bordered="false" dis-hover class="ivu-mt">
            <TableFormNew ref="tableFormNewRef" :formList="formList" @onSubmit="onSubmit" @onReset="onReset">
                <Button class="ivu-ml-8" slot="extraBtn" type="error" @click="exportData">导出</Button>
            </TableFormNew>
            <Button type="primary" ghost @click="onModify" class="mb-10"> 批量修改维护人 </Button>
            <TablePage
                :list="tableConfig"
                @onPageChange="onPageChange"
                @onPageSizeChange="onPageSizeChange"
                @onSelectionChange="handleSelection"
            />
        </Card>
        <ModalProtector ref="modalProtectorRef" @success="getData" />
        <SalesmanImport ref="salesmanImportRef" @uploadSuccess="getData" />
    </div>
</template>

<script>
import TableFormNew from '@/components/tableFormNew';
import TablePage from '@/components/tablePage';
import ModalProtector from './components/ModalProtector';
import SalesmanImport from './components/SalesmanImport';
import {
    apiStoreList,
    apiExportStore,
    apiSalesmanList,
    apiDictionaryListByType,
    apiSetStoreTourTopdata,
} from '@/api/store';
import { apiQueryAreasByList } from '@/api/common';
import { clearInvalidKey } from '@/libs/util.tool';
import { jumpPage, renderButton } from '@/libs/util';
import { signStatusDict } from '@/dicts';
import { customerManageColumns } from './const';

export default {
    components: { TableFormNew, TablePage, ModalProtector, SalesmanImport },
    data() {
        return {
            formData: {},
            showModal: false,
            tableSelection: [],
            tableConfig: {
                current: 1,
                size: window.$pageSize,
                total: 0,
                columns: [
                    ...customerManageColumns,
                    {
                        title: '操作',
                        key: 'oprate',
                        minWidth: 170,
                        fixed: 'right',
                        render: (h, p) => {
                            const storeTourTop = {
                                true: {
                                    text: '取消置顶',
                                    type: '',
                                    showTip: false,
                                },
                                false: {
                                    text: '巡店置顶',
                                    type: 'warning',
                                    showTip: true,
                                },
                            }[p.row.isStoreTourTop];
                            const setTopBtn = renderButton(
                                h,
                                {
                                    text: storeTourTop.text,
                                    type: storeTourTop.type,
                                },
                                () => {
                                    this.onSetTop(p.row);
                                },
                            );
                            return [
                                renderButton(h, { text: '详情', type: 'primary' }, () => {
                                    this.toDetail(p.row.id);
                                }),
                                storeTourTop.showTip
                                    ? h(
                                          'Poptip',
                                          {
                                              props: {
                                                  trigger: 'hover',
                                                  content: '操作后该门店将在业务通APP-巡店管理及店检中心置顶展示',
                                                  wordWrap: true,
                                                  transfer: true,
                                                  placement: 'bottom',
                                              },
                                          },
                                          [setTopBtn],
                                      )
                                    : setTopBtn,
                            ];
                        },
                    },
                ],
                data: [],
                loading: false,
            },
            formList: [
                { type: 'input', label: '门店名称', prop: 'storeName' },
                { type: 'input', label: '联系人名称', prop: 'ownerName' },
                { type: 'input', label: '联系方式', prop: 'ownerPhone' },
                { type: 'cascader', label: '地区', prop: 'areaIds', options: [] },
                { type: 'select', label: '门店维护人', prop: 'salesmanId', options: [] },
                { type: 'select', label: '标签', prop: 'tagDictionaryId', options: [] },
                {
                    type: 'select',
                    label: '新零售签约',
                    prop: 'signStatus',
                    options: signStatusDict,
                },
                {
                    type: 'select',
                    label: '巡店置顶',
                    prop: 'isStoreTourTop',
                    options: [
                        { value: 1, label: '是' },
                        { value: 0, label: '否' },
                    ],
                    isConvertToBoolean: true,
                },
            ],
        };
    },
    mounted() {
        this.getArea();
    },
    activated() {
        this.getSaleMan();
        this.getTagList();
        this.getData();
    },
    methods: {
        // 打开导入弹窗
        onImport() {
            this.$refs['salesmanImportRef'].open();
        },
        // 查找formList
        getFormItem(prop) {
            return this.formList.find((item) => item.prop === prop);
        },
        // 获取地区
        async getArea() {
            const res = await apiQueryAreasByList();
            const formatArea = (list = []) =>
                list.map((item) => {
                    const children = item.childCategoryList ? formatArea(item.childCategoryList) : [];
                    return {
                        label: item.gaodeAreaDTO.name,
                        value: item.gaodeAreaDTO.id,
                        children,
                    };
                });
            this.getFormItem('areaIds').options = formatArea(res);
        },
        // 获取门店维护人
        async getSaleMan() {
            const res = await apiSalesmanList();
            const options = (res || []).map((item) => {
                return {
                    value: item.id,
                    label: `${item.name}(${item.phone}/${item.isEnabled ? '启用' : '禁用'})`,
                };
            });
            this.getFormItem('salesmanId').options = [{ label: '无维护人', value: '0' }, ...options];
        },
        // 获取标签列表
        async getTagList() {
            const res = await apiDictionaryListByType({ dataType: 5 }); // 5：门店标签
            this.getFormItem('tagDictionaryId').options = (res || []).map((item) => {
                return {
                    value: item.id,
                    label: item.dataValue,
                };
            });
        },
        // 获取参数
        getParams() {
            const { areaIds, ...rest } = this.$refs['tableFormNewRef'].getFormData() || {};
            const params = {
                ...rest,
                provinceOrCityOrAreaId: areaIds?.at(-1),
                current: this.tableConfig.current,
                size: this.tableConfig.size,
            };
            return clearInvalidKey(params);
        },
        // 获取列表数据
        async getData() {
            try {
                this.tableConfig.loading = true;
                const res = await apiStoreList(this.getParams());
                this.tableConfig.total = Number(res.total);
                const selectedIds = this.tableSelection.map((item) => item.id);
                this.tableConfig.data = (res.records || []).map((item) => {
                    return {
                        ...item,
                        _checked: selectedIds.includes(item.id),
                    };
                });
            } finally {
                this.tableConfig.loading = false;
            }
        },
        // 导出
        async exportData() {
            try {
                this.$Message.loading({ content: '导出中...', duration: 0 });
                await apiExportStore(this.getParams());
                this.$Modal.confirm({
                    title: '提示',
                    content: `您好！“${this.$route.meta.title}”正在导出，请稍后在“导出列表”模块中查看，谢谢！`,
                    onOk: () => {
                        console.log('export');
                    },
                });
            } finally {
                this.$Message.destroy();
            }
        },
        // 查询
        onSubmit() {
            this.tableConfig.current = 1;
            this.getData();
        },
        // 重置
        onReset() {
            this.tableConfig.current = 1;
            this.tableConfig.size = window.$pageSize;
            this.getData();
        },
        // current-change
        onPageChange(val) {
            this.tableConfig.current = val;
            this.getData();
        },
        // size-change
        onPageSizeChange(val) {
            this.tableConfig.current = 1;
            this.tableConfig.size = val;
            this.getData();
        },
        // 跳转详情
        toDetail(id) {
            jumpPage({ path: '/sub-vue2/customer/manage/storeDetail', params: { id: id } });
        },
        // 设置巡店置顶
        async onSetTop(row) {
            const params = { storeId: row.id, isStoreTourTop: !row.isStoreTourTop };
            await apiSetStoreTourTopdata(params);
            this.getData();
        },
        // table 选择改变
        handleSelection(val) {
            const currentPageIds = this.tableConfig.data.map((item) => item.id);
            const selectedIds = val.map((item) => item.id);
            // 先删除当前页里被取消选中的
            this.tableSelection = this.tableSelection.filter((item) => {
                if (currentPageIds.includes(item.id)) {
                    return selectedIds.includes(item.id);
                }
                return true;
            });
            // 再添加新选中的
            val.forEach((item) => {
                if (!this.tableSelection.find((i) => i.id === item.id)) {
                    this.tableSelection.push(item);
                }
            });
        },
        // 修改维护人
        onModify() {
            if (!this.tableSelection.length) {
                this.$Notice.warning({ title: '请勾选门店' });
                return;
            }
            const ids = this.tableSelection.map((item) => item.id).join(',');
            this.changeSalesMan('BATCH', { ids });
        },
        // 打开修改弹窗
        changeSalesMan(updateType, row) {
            this.$refs['modalProtectorRef'].open(updateType, row);
        },
    },
};
</script>
